<template>
    <sidebar-left>
        <slot slot="sidebar-left">
            <SideMenu :SideMenuLinks="sideMenuLinks"></SideMenu>
        </slot>
        <slot slot="main">
            <div class="u-max-700 u-center-block">
                <v-loading type="global" :data="Personal"></v-loading>
                <page-header>
                    <slot slot="title">{{ $t('customer.account.edit.title') }}</slot>
                    <slot slot="description">
                        <p>{{ $t('intro.customer_account') }}</p>
                    </slot>
                </page-header>

                <!-- PERSONAL -->
                <section class="c-form-row o-page-bottom-spacing--medium">
                    <form action="" class="c-form c-form--horizontal">

                        <h2 class="c-title--border u-h3">
                            {{ $t('customer.account.edit.form.personal.title') }}
                        </h2>

                        <v-form-row>
                            <v-input v-model="Personal.FirstName" name="personal_first_name" :fluid="true" :error="Validate.FirstName">
                                <slot slot="label">{{ $t('users.first_name') }}</slot>
                            </v-input>
                        </v-form-row>

                        <v-form-row>
                            <v-input v-model="Personal.LastName" name="personal_last_name" :fluid="true" :error="Validate.LastName">
                                <slot slot="label">{{ $t('users.last_name') }}</slot>
                            </v-input>
                        </v-form-row>

                        <v-form-row>
                            <v-custom-select v-model="selectedFunction"
                                             name="function"
                                             :options="FunctionOptions"
                                             :fluid="true"
                                             option-name="content"
                                             option-value="value"
                                             track-by="value"
                                             search-label="content">
                                <slot slot="label">{{ $t('customer.account.edit.form.personal.function.label') }}</slot>
                            </v-custom-select>
                        </v-form-row>

                        <v-form-row>
                            <v-input v-model="Personal.PhoneNumber" name="personal_phone" type="tel" :fluid="true" :error="Validate.PhoneNumber">
                                <slot slot="label">{{ $t('customer.account.edit.form.personal.phone.label') }}</slot>
                            </v-input>
                        </v-form-row>

                        <v-form-row>
                            <v-input v-model="Personal.Email" name="personal_email" type="email" :fluid="true"
                                     :error="Validate.Email">
                                <slot slot="label">{{ $t('customer.account.edit.form.personal.email.label') }}</slot>
                            </v-input>
                        </v-form-row>

                        <v-form-row>
                            <div class="c-form-item c-form-item--horizontal c-form-item--fluid">
                                <label class="c-label">{{ $t('customer.account.edit.form.personal.profile_picture.label') }}</label>
                                <div class="u-inline-block u-pos-relative" style="width: 200px">
                                    <v-picture-input
                                            :width="200"
                                            :height="200"
                                            :selected="Personal.Photo"
                                            @changed="setProfilePicture">
                                    </v-picture-input>
                                </div>
                            </div>
                        </v-form-row>

                        <v-form-row>
                            <div class="c-form-item c-form-item--horizontal">
                                <v-label></v-label>
                                <button @click.prevent="submitPersonal" class="c-button c-button--alpha">
                                    {{ $t('customer.account.edit.form.personal.submit') }}
                                </button>
                            </div>
                        </v-form-row>
                    </form>
                </section>

                <!-- PASSWORD -->
                <section class="c-form-row o-page-bottom-spacing--medium">
                    <form action="" class="c-form c-form--horizontal">

                        <h2 class="c-title--border u-h3">
                            {{ $t('customer.account.edit.form.password.title') }}
                        </h2>

                        <v-form-row>
                            <v-input v-model="Password.OldPassword" name="old_password" type="password" :fluid="true"
                                     :error="Validate.OldPassword">
                                <slot slot="label">{{ $t('customer.account.edit.form.password.old_password.label') }}</slot>
                            </v-input>
                        </v-form-row>

                        <v-form-row>
                            <v-input v-model="Password.NewPassword" name="new_password" type="password" :fluid="true"
                                     :error="Validate.NewPassword">
                                <slot slot="label">{{ $t('customer.account.edit.form.password.new_password.label') }}</slot>
                            </v-input>
                        </v-form-row>

                        <v-form-row>
                            <v-input v-model="Password.ConfirmPassword" name="confirm_password" type="password" :fluid="true"
                                     :error="Validate.ConfirmPassword">
                                <slot slot="label">{{ $t('customer.account.edit.form.password.confirm_password.label') }}</slot>
                            </v-input>
                        </v-form-row>

                        <div class="c-form-row">
                            <div class="c-form-item c-form-item--horizontal c-form-item--fluid">
                                <label class="c-label"></label>
                                <div class="u-inline-block u-pos-relative u-fullwidth">
                                    <p class="u-font-small">{{ $t('customer.account.register.password_minimum_length') }}</p>
                                </div>
                            </div>
                        </div>

                        <v-form-row>
                            <div class="c-form-item c-form-item--horizontal">
                                <v-label></v-label>
                                <button @click.prevent="submitPassword" class="c-button c-button--alpha" :disabled="validatePassword">
                                    {{ $t('customer.account.edit.form.password.title') }}
                                </button>
                            </div>
                        </v-form-row>
                    </form>
                </section>

                <!-- COMPANY -->
                <section v-if="isCustomer" class="c-form-row o-page-bottom-spacing--medium">
                    <form action="" class="c-form c-form--horizontal">

                        <h2 class="c-title--border u-h3">
                            {{ $t('customer.account.edit.form.company.title') }}
                        </h2>

                        <v-form-row>
                            <v-input v-model="Extra.Name" name="company_name" :fluid="true" :error="Validate.Name">
                                <slot slot="label">{{ $t('customer.account.edit.form.company.name.label') }}</slot>
                            </v-input>
                        </v-form-row>

                        <v-form-row>
                            <v-input v-model="Extra.CommercialName" name="commercial_name" :fluid="true"
                                     :error="Validate.CommercialName">
                                <slot slot="label">{{ $t('customer.account.edit.form.company.commercial_name.label') }}
                                </slot>
                            </v-input>
                        </v-form-row>

                        <v-form-row>
                            <v-input v-model="Extra.Phone" name="company_phone" type="tel" :fluid="true" :error="Validate.Phone">
                                <slot slot="label">{{ $t('customer.account.edit.form.company.phone.label') }}</slot>
                            </v-input>
                        </v-form-row>

                        <v-form-row>
                            <v-input v-model="Extra.Email" name="company_email" type="email" :fluid="true"
                                     :error="Validate.Email">
                                <slot slot="label">{{ $t('customer.account.edit.form.company.email.label') }}</slot>
                            </v-input>
                        </v-form-row>

                        <v-form-row>
                            <v-switch v-model="Extra.UseEmailInvoiceTemplate" name="digital_invoices">
                                <slot slot="label">{{ $t('customer.account.edit.form.company.digital_invoices.label') }}
                                </slot>
                            </v-switch>
                        </v-form-row>

                        <v-form-row>
                            <v-input v-model="Extra.EmailInvoice" name="email_invoices" type="email" :fluid="true"
                                     :error="Validate.EmailInvoice">
                                <slot slot="label">{{ $t('customer.account.edit.form.company.email_invoicing.label') }}
                                </slot>
                            </v-input>
                        </v-form-row>

                        <v-form-row>
                            <div class="c-form-item c-form-item--horizontal">
                                <v-label></v-label>
                                <button @click.prevent="submitExtra" class="c-button c-button--alpha">
                                    {{ $t('customer.account.edit.form.company.submit') }}
                                </button>
                            </div>
                        </v-form-row>
                    </form>
                </section>

                <!-- EXTRA -->
                <section v-if="isCustomer" class="c-form-row o-page-bottom-spacing--medium">
                    <form action="" class="c-form c-form--horizontal">

                        <h2 class="c-title--border u-h3">
                            {{ $t('customer.account.edit.form.extra.title') }}
                        </h2>

                        <v-form-row>
                            <v-input v-model="Extra.FacebookFollowers" name="name" :fluid="true" :error="Validate.Facebook">
                                <slot slot="label">{{ $t('customer.account.edit.form.extra.facebook.label') }}</slot>
                            </v-input>
                        </v-form-row>

                        <v-form-row>
                            <v-input v-model="Extra.InstagramFollowers" name="instagram" :fluid="true" :error="Validate.Instagram">
                                <slot slot="label">{{ $t('customer.account.edit.form.extra.instagram.label') }}</slot>
                            </v-input>
                        </v-form-row>

                        <v-form-row>
                            <v-input v-model="Extra.Url" name="website" type="url" placeholder="https://www." :fluid="true"
                                     :error="Validate.Url">
                                <slot slot="label">{{ $t('customer.account.edit.form.extra.website.label') }}</slot>
                            </v-input>
                        </v-form-row>

                        <v-form-row>
                            <v-input v-model="Extra.WebshopUrl" name="email" :fluid="true" placeholder="https://www." :error="Validate.WebshopUrl">
                                <slot slot="label">{{ $t('customer.account.edit.form.extra.webshop_url.label') }}</slot>
                            </v-input>
                        </v-form-row>

                        <v-form-row>
                            <v-input v-model="Extra.PointOfSaleSystem" name="cash_register_system" :fluid="true"
                                     :error="Validate.PointOfSaleSystem">
                                <slot slot="label">{{ $t('customer.account.edit.form.extra.cash_register_system.label') }}
                                </slot>
                            </v-input>
                        </v-form-row>

                        <v-form-row>
                            <div class="c-form-item c-form-item--horizontal">
                                <v-label></v-label>
                                <button @click.prevent="submitExtra" class="c-button c-button--alpha">
                                    {{ $t('customer.account.edit.form.extra.submit') }}
                                </button>
                            </div>
                        </v-form-row>
                    </form>
                </section>

            </div>
        </slot>
    </sidebar-left>
</template>

<script>
    import VLoading from '@/components/Loading'
    import VTextarea from '@/components/form/Textarea'
    import VInput from '@/components/form/Input'
    import VPictureInput from '@/components/form/PictureInput'
    import VSelect from '@/components/form/Select'
    import VCustomSelect from '@/components/form/CustomSelect'
    import VFormRow from '@/components/form/Row'
    import VLabel from '@/components/form/Label'
    import VTargetListSelect from '@/components/form/targetList/TargetListSelect'
    import VSwitch from '@/components/form/Switch'
    import PageHeader from '@/components/common/PageHeader'
    import SideMenu from '@/components/common/SideMenu'
    import SidebarLeft from '@/components/layout/SidebarLeft'

    // Store
    import {mapGetters} from 'vuex'

    export default {
        components: {
            PageHeader,
            SideMenu,
            SidebarLeft,
            VTextarea,
            VSelect,
            VCustomSelect,
            VLabel,
            VInput,
            VPictureInput,
            VFormRow,
            VTargetListSelect,
            VSwitch,
            VLoading
        },
        data() {
            return {
                Password: {
                    OldPassword: '',
                    NewPassword: '',
                    ConfirmPassword: ''
                },
                Validate: {},
                FunctionOptions: [
                    {
                        value: 0,
                        content:this.$t('global.purchase')
                    },
                    {
                        value: 1,
                        content: this.$t('global.marketing')
                    },
                    {
                        value: 2,
                        content:this.$t('global.director')
                    },
                    {
                        value: 3,
                        content: this.$t('global.shop_assistant')
                    },
                    {
                        value: 4,
                        content:this.$t('global.accountancy')
                    }
                ],
            }
        },
        created() {
            this.$store.dispatch('getUser');
            this.$store.dispatch('getCustomerById', this.$store.getters.userInfo.customerKey);
        },
        computed: {
            ...mapGetters({
                Personal: 'userPersonalData',
                Extra: 'Customer',
            }),
            selectedFunction: {
                get () {
                    let output;
                    output = this.FunctionOptions.find((obj) => {
                        return obj.value === this.Personal.Function;
                    });

                    if (output) {
                        this.$set(this.Personal, 'Function', output.value);
                    }

                    return output ? output : { };
                },
                set (value) {
                    this.$set(this.Personal, 'Function', value.value);
                    return value;
                }
            },
            validatePassword() {
                return (this.Password.ConfirmPassword === '' || this.Password.NewPassword === '' || this.Password.OldPassword === '')
            },
            sideMenuLinks() {
                let menu = [
                    {
                        title: 'customer.account.edit.title',
                        route: '/customer/account/edit',
                    }
                ];

                if(this.isCustomer) {
                    menu.push({
                        title: 'customer.account.users.title',
                        route: '/customer/account/users'
                    });
                    menu.push({
                        title: 'customer.account.addresses.title',
                        route: '/customer/account/addresses'
                    });
                }

                if(this.isCustomer || this.isMarketing) {
                    menu.push({
                        title: 'customer.account.history.title',
                        route: '/customer/account/history'
                    });

                    menu.push({
                        title: 'customer.account.backorders.title',
                        route: '/customer/account/backorders'
                    })
                }

                if(this.isCustomer) {
                    menu.push({
                        title: 'customer.account.invoices.title',
                        route: '/customer/account/invoices'
                    });
                }

              return menu;
            },
            isCustomer() {
                return this.$store.getters.isCustomer;
            },
            isPersoneel() {
                return this.$store.getters.isPersoneel;
            },
            isMarketing() {
                return this.$store.getters.isMarketing;
            }
        },
        methods: {
            validateForm(response) {
                let errors = response;

                Object.keys(errors).forEach(function(key) {
                    key = key.replace('model.', '');
                    errors[key] = errors['model.'+key][0];
                    delete errors['model.'+key];
                });

                this.Validate = errors;
            },
            submitPersonal() {
                this.$store.dispatch('sendUser', this.Personal).then(() => {
                    this.notifySuccess('Persoonlijke gegevens');
                    this.Validate = {};
                });
            },
            submitPassword() {
                this.$store.dispatch('changePassword', this.Password).then(() => {
                    this.notifySuccess('Wachtwoord gewijzigd');
                    this.Validate = {};
                }).catch((e) => {
                    this.notifyError(e.Message);
                    this.validateForm(e.ModelState);
                });
            },
            submitExtra () {
                this.$store.dispatch('updateCustomerById', {
                    body: this.Extra
                }).then(() => {
                    this.notifySuccess('Gegevens gewijzigd');
                    this.Validate = {};
                }).catch((e) => {
                    this.notifyError(e.Message);
                    this.validateForm(e.ModelState);
                });
            },

            setProfilePicture(picture) {
                this.$set(this.Personal, 'Photo', picture)
            },

            notifySuccess(title) {
                this.$notify({
                    group: 'global',
                    title: title,
                    text: this.$t('global.saved'),
                    type: 'success',
                    duration: 4000
                });
            },
            notifyError(title) {
                this.$notify({
                    group: 'global',
                    title: title,
                    text: this.$t('global.error_occured'),
                    type: 'error',
                    duration: 4000
                });
            }
        }
    }
</script>



// WEBPACK FOOTER //
// src/views/customer/account/Edit.vue